<template>
  <label
    class="toggle-switch"
    :class="{
      disabled: disabled,
      'size-small': size === 'small',
      'size-large': size === 'large'
    }"
    :style="{
      '--switch-on-color': switchOnColor,
      '--switch-off-color': switchOffColor
    }"
  >
    <input
      :key="`toggle-switch-${modelValue}-${disabled}`"
      :id="inputId"
      type="checkbox"
      :checked="modelValue"
      :disabled="disabled"
      @click.prevent="handleToggle"
    />
    <span class="slider" :class="{ round: rounded }"></span>
    <span v-if="label" class="toggle-label">{{ label }}</span>
  </label>
</template>

<script>
import { TOGGLE_SWITCH_COLORS } from 'src/utils/constants/common';
export default {
  name: 'ToggleSwitch',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    // random is safe to use in this case
    inputId: {
      type: String,
      default: () => `toggle-${Math.random().toString(36).substring(2, 11)}` // NOSONAR
    },
    rounded: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'medium',
      validator: value => ['small', 'medium', 'large'].includes(value)
    },
    switchOnColor: {
      type: String,
      default: TOGGLE_SWITCH_COLORS.ON
    },
    switchOffColor: {
      type: String,
      default: TOGGLE_SWITCH_COLORS.OFF
    }
  },
  emits: ['update:modelValue', 'change'],
  methods: {
    handleToggle() {
      if (this.disabled) return;

      const newValue = !this.modelValue;
      this.$emit('update:modelValue', newValue);
      this.$emit('change', newValue);
    }
  }
};
</script>

<style scoped>
.toggle-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: var(--md-spacing-unit--xs, 8px);

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* Small size */
  &.size-small {
    width: 28px;
    height: 16px;

    .slider:before {
      height: 12px;
      width: 12px;
      left: 2px;
      bottom: 2px;
    }

    input:checked + .slider:before {
      transform: translateX(12px);
    }
  }

  /* Medium size (default) */
  &:not(.size-small):not(.size-large) {
    width: 36px;
    height: 20px;

    .slider:before {
      height: 15px;
      width: 16px;
      left: 3px;
      bottom: 2.5px;
    }

    input:checked + .slider:before {
      transform: translateX(14px);
    }
  }

  /* Large size */
  &.size-large {
    width: 44px;
    height: 24px;

    .slider:before {
      height: 18px;
      width: 20px;
      left: 3px;
      bottom: 3px;
    }

    input:checked + .slider:before {
      transform: translateX(18px);
    }
  }

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;

    &:checked + .slider {
      background-color: var(--switch-on-color, #1a73e8);
    }

    &:disabled + .slider {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: var(--switch-off-color, #c3cad4);
    transition: 0.4s;

    &.round {
      border-radius: 34px;
    }

    &:before {
      position: absolute;
      content: '';
      background-color: white;
      transition: 0.4s;
      border-radius: 50%;
    }
  }

  .toggle-label {
    font-size: var(--md-font-size--body2, 14px);
    color: var(--md-color--spirits--900, #1e242c);
    white-space: nowrap;
  }
}
</style>
